import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { UserProfile } from '../models/user.model';
import { API_ENDPOINTS } from '@core/constants/api-endpoints.constants';
import { Result, SuccessResponse } from '../models/common.model';
import { environment } from '@environments/environment';
import { coerceApiResult, mapHttpErrorToResult } from '@shared/utils/api-response.utils';

@Injectable({ providedIn: 'root' })
export class UserService {
  userProfile: UserProfile | null = null;

  constructor(private apiService: ApiService) {}

  getUserProfileByUid(uid: string): Observable<Result<UserProfile>> {
    return this.apiService
      .get<SuccessResponse<UserProfile>>(`${API_ENDPOINTS.AUTH.REGISTER}/${uid}`)
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        catchError((error: { message?: string; code?: string }) => {
          if (environment.consoleFlag) {
            console.error('User profile retrieval failed:', error);
          }

          return of({
            success: false as const,
            message: error.message || 'User retrieval failed',
            errorCode: error.code,
          });
        }),
      );
  }

  getUserProfile(): UserProfile | null {
    return this.userProfile;
  }

  clearUserProfile(): void {
    this.userProfile = null;
  }

  updateUserProfile(profile: UserProfile, uid: string): Observable<Result<UserProfile>> {
    return this.apiService
      .put<SuccessResponse<UserProfile> | Result<UserProfile>>(API_ENDPOINTS.USERS.VIEW(uid), profile)
      .pipe(
        map((response) => {
          if (response && typeof response === 'object' && 'success' in response) {
            return coerceApiResult<UserProfile>(response, 'UPDATE_USER');
          }

          if (response && typeof response === 'object' && 'data' in response) {
            return { success: true as const, data: (response as SuccessResponse<UserProfile>).data };
          }

          return { success: true as const, data: response as UserProfile };
        }),
        catchError((error) => of(mapHttpErrorToResult<UserProfile>(error, 'UPDATE_USER'))),
      );
  }
}
